import { c as _c } from "react/compiler-runtime";
import { Button } from '@achra/ui/button';
import { Dialog, DialogHeader, DialogOverlay, DialogPortal, DialogTitle } from '@achra/ui/dialog';
import { Input } from '@achra/ui/input';
import { cn } from '@achra/ui/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowLeft, Search, X } from 'lucide-react';
import SimpleBar from 'simplebar-react';
import type { ScopeOfWork_Deliverable } from '@/modules/__generated__/graphql/switchboard-generated';
import { DeliverableStatusChip } from '@/modules/shared/components/chips/deliverable-status-chip';
import { FilterDrawer } from '@/modules/shared/components/filter-drawer/filter-drawer';
import { StatusSelectDelivarableDrawer, StatusSelectDeliverable } from './key-result-filters';
import { KeyResultItem } from './key-result-item';
import { useDeliverableFilters } from './use-deliverable-filters';
interface KeyResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliverables: ScopeOfWork_Deliverable[];
}
function KeyResultsModal(t0) {
  const $ = _c(51);
  const {
    isOpen,
    onClose,
    deliverables
  } = t0;
  let t1;
  if ($[0] !== deliverables) {
    t1 = {
      deliverables
    };
    $[0] = deliverables;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    search,
    statuses,
    onReset,
    setSearch,
    setStatuses,
    filteredDeliverables
  } = useDeliverableFilters(t1);
  let t2;
  if ($[2] !== onClose || $[3] !== onReset) {
    t2 = () => {
      onClose();
      onReset();
    };
    $[2] = onClose;
    $[3] = onReset;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleClose = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DialogOverlay className="z-170!" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("no-scrollbar top-0 left-0 z-180 mb-0 h-screen max-h-full! w-screen max-w-full translate-x-0 translate-y-0 content-start items-start gap-0 overflow-hidden overscroll-contain rounded-none p-0", "sm:top-[50%] sm:left-[50%] sm:mb-12 sm:h-auto sm:w-full sm:max-w-xl! sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg md:max-w-172! lg:max-w-216! xl:max-h-173.5! xl:max-w-279.5!", "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed grid border shadow-lg duration-200");
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArrowLeft className="size-4" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onClose) {
    t6 = <Button variant="outline" size="icon" className="flex items-center sm:hidden" onClick={onClose}>{t5}</Button>;
    $[8] = onClose;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <DialogTitle className="text-foreground flex flex-col text-lg/[120%] font-bold sm:flex-row sm:gap-2">Front-end Development<p className="text-foreground/50 text-start text-base/6 font-semibold sm:text-start">Key Results</p></DialogTitle>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6) {
    t8 = <div className="flex items-start gap-2 sm:items-center">{t6}{t7}</div>;
    $[11] = t6;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <X className="h-5 w-5" />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== onClose) {
    t10 = <Button variant="ghost" size="icon" onClick={onClose} className="text-popover-foreground hover:text-popover-foreground/80 hidden sm:flex">{t9}</Button>;
    $[14] = onClose;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8) {
    t11 = <div className="flex w-full flex-row items-center justify-between">{t8}{t10}</div>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== setSearch) {
    t13 = e => {
      setSearch(e.target.value);
    };
    $[20] = setSearch;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = cn("pl-10", "border-accent bg-accent shadow-none", "focus:border-accent focus:ring-0 focus:outline-none", "focus-visible:border-accent focus-visible:ring-0 focus-visible:ring-offset-0", "placeholder:text-accent-foreground/30 text-sm", "[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-cancel-button]:appearance-none");
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== search || $[24] !== t13) {
    t15 = <div className="relative flex-1">{t12}<Input type="text" placeholder="Search..." value={search} onChange={t13} className={t14} /></div>;
    $[23] = search;
    $[24] = t13;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== setStatuses || $[27] !== statuses) {
    t16 = <div className="hidden sm:block"><StatusSelectDeliverable statuses={statuses} setStatuses={setStatuses} className="w-56" /></div>;
    $[26] = setStatuses;
    $[27] = statuses;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== setStatuses || $[30] !== statuses) {
    t17 = <StatusSelectDelivarableDrawer statuses={statuses} setStatuses={setStatuses} />;
    $[29] = setStatuses;
    $[30] = statuses;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== onReset || $[33] !== t17) {
    t18 = <div className="flex sm:hidden"><FilterDrawer className="z-200" onReset={onReset}>{t17}</FilterDrawer></div>;
    $[32] = onReset;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t15 || $[36] !== t16 || $[37] !== t18) {
    t19 = <div className="flex items-center gap-6">{t15}{t16}{t18}</div>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t11 || $[40] !== t19) {
    t20 = <DialogHeader className="border-input flex flex-col border-b px-4 py-3">{t11}{t19}</DialogHeader>;
    $[39] = t11;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== filteredDeliverables) {
    t21 = <SimpleBar className="px-4 py-3 sm:h-150 sm:px-4 sm:py-3" autoHide={false}>{filteredDeliverables.length === 0 ? <div className="flex h-full items-center justify-center"><p className="text-muted-foreground text-lg">Results not found...</p></div> : <div className="space-y-3 pb-3">{filteredDeliverables.map(_temp2)}</div>}</SimpleBar>;
    $[42] = filteredDeliverables;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t20 || $[45] !== t21) {
    t22 = <DialogPortal>{t3}<DialogPrimitive.Content className={t4}>{t20}{t21}</DialogPrimitive.Content></DialogPortal>;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== handleClose || $[48] !== isOpen || $[49] !== t22) {
    t23 = <Dialog open={isOpen} onOpenChange={handleClose}>{t22}</Dialog>;
    $[47] = handleClose;
    $[48] = isOpen;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  return t23;
}
function _temp2(deliverable) {
  return <div key={deliverable.id}><div className="bg-accent mb-3 flex items-center justify-between rounded-md p-2"><div className="flex items-center gap-1"><span className="text-foreground/30 text-sm/4.5 font-semibold">{deliverable.code}</span><h3 className="text-foreground text-sm/5.5 font-semibold">{deliverable.title}</h3></div><div className="ml-1 flex"><DeliverableStatusChip status={deliverable.status} /></div></div><ul className="flex flex-col gap-3 sm:pl-2 md:pl-4 xl:pl-4">{deliverable.keyResults.map(_temp)}</ul></div>;
}
function _temp(keyResult) {
  return <KeyResultItem key={keyResult.id} keyResult={keyResult} />;
}
export { KeyResultsModal };